import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import api from '../api/axios';
import './PublicStatusUpdate.css';
import StatusIcon from '../components/StatusIcon';
import { compressImage } from '../utils/imageCompression';



const PublicStatusUpdate = () => {
    const { token } = useParams();
    const [instance, setInstance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [compressing, setCompressing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [expired, setExpired] = useState(false);

    const [formData, setFormData] = useState({
        status: '',
        reporterInfo: {
            name: '',
            phone: '',
            email: ''
        },
        notes: '',
        geolocation: null,
        fieldValues: {},
        photos: []
    });

    useEffect(() => {
        fetchInstance();
    }, [token]);

    const fetchInstance = async () => {
        try {
            // Fetch tracking instance by trackingToken
            const response = await api.get(`/public/tracking/${token}`);
            if (response.data.data.expired) {
                setExpired(true);
            } else {
                const fetchedInstance = response.data.data.instance;
                setInstance(fetchedInstance);
                
                // If it's a checklist, auto-select the first available status 
                // so the form can be submitted without seeing the status buttons
                if (fetchedInstance.templateSnapshot?.templateType === 'checklist' && fetchedInstance.templateSnapshot?.statuses?.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        status: fetchedInstance.templateSnapshot.statuses[0].name
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching instance:', error);
            setInstance(null);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (statusName) => {
        setFormData({
            ...formData,
            status: formData.status === statusName ? '' : statusName
        });
    };

    const handleReporterChange = (e) => {
        setFormData({
            ...formData,
            reporterInfo: {
                ...formData.reporterInfo,
                [e.target.name]: e.target.value
            }
        });
    };

    const handleFieldChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            fieldValues: {
                ...prev.fieldValues,
                [fieldName]: value
            }
        }));
    };

    const handlePhotoChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setCompressing(true);
        try {
            const compressedFiles = await Promise.all(
                files.map(file => compressImage(file))
            );

            setFormData(prev => ({
                ...prev,
                photos: [...prev.photos, ...compressedFiles]
            }));
        } catch (error) {
            console.error('Błąd kompresji zdjęć:', error);
            alert('Wystąpił błąd podczas przygotowywania zdjęć. Spróbuj mniejszych plików.');
        } finally {
            setCompressing(false);
        }
    };

    const removePhoto = (index) => {
        setFormData(prev => ({
            ...prev,
            photos: prev.photos.filter((_, i) => i !== index)
        }));
    };

    const getGeolocation = () => {
        return new Promise((resolve) => {
            if ('geolocation' in navigator) {
                const timeoutId = setTimeout(() => resolve(null), 5000); // 5s timeout

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        clearTimeout(timeoutId);
                        resolve({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy
                        });
                    },
                    (error) => {
                        clearTimeout(timeoutId);
                        console.warn('Geolocation error:', error);
                        resolve(null);
                    },
                    { timeout: 5000 }
                );
            } else {
                resolve(null);
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.status) {
            alert('Proszę wybrać status');
            return;
        }

        setSubmitting(true);

        try {
            const geolocation = await getGeolocation();

            // Create FormData for file upload
            const submitData = new FormData();
            submitData.append('status', formData.status);
            submitData.append('reporterInfo', JSON.stringify(formData.reporterInfo));
            submitData.append('notes', formData.notes || '');
            if (geolocation) {
                submitData.append('geolocation', JSON.stringify(geolocation));
            }
            // Extract specific field photos
            const valuesToSend = { ...formData.fieldValues };
            Object.keys(valuesToSend).forEach(key => {
                if (valuesToSend[key] instanceof File) {
                    submitData.append(`field_photo_${key}`, valuesToSend[key]);
                    delete valuesToSend[key];
                }
            });
            submitData.append('fieldValues', JSON.stringify(valuesToSend));

            // Append photos
            formData.photos.forEach((photo) => {
                submitData.append('photos', photo);
            });

            const response = await api.post(`/public/tracking/${token}/update`, submitData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log('Status update response:', response.data);
            setSuccess(true);

            // Refresh instance to show new status
            setTimeout(() => {
                fetchInstance();
                setSuccess(false);
                setFormData({
                    status: '',
                    reporterInfo: { name: '', phone: '', email: '' },
                    notes: '',
                    geolocation: null,
                    fieldValues: {},
                    photos: []
                });
            }, 5000); // Increased from 2000 to 5000ms
        } catch (error) {
            console.error('Error updating status:', error);
            console.error('Error details:', error.response?.data);

            // Check if tracking is deactivated
            if (error.response?.data?.isDeactivated) {
                const deactivationDate = new Date(error.response.data.deactivationDate).toLocaleString('pl-PL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                alert(`To śledzenie zostało dezaktywowane w dniu ${deactivationDate}.\n\nNie można już wprowadzać zmian statusu.`);
                return;
            }

            const errorMsg = error.response?.data?.message || 'Błąd podczas aktualizacji statusu.';
            const errorDetails = error.response?.data?.error ? `\n\nSzczegóły: ${typeof error.response.data.error === 'object' ? JSON.stringify(error.response.data.error, null, 2) : error.response.data.error}` : '';
            alert(`${errorMsg}${errorDetails}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="public-update">
                <div className="container">
                    <div className="spinner-container">
                        <div className="spinner"></div>
                        <p>Ładowanie...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (expired) {
        return (
            <div className="public-update">
                <div className="container">
                    <div className="error-state card">
                        <div className="error-icon">⌛</div>
                        <h2>Status wygasł</h2>
                        <p className="text-muted">
                            Ten link do śledzenia już wygasł ze względów bezpieczeństwa.
                        </p>
                        <a
                            href="https://sprawdzstatus.pl"
                            className="btn btn-primary"
                            style={{ marginTop: '1rem', textDecoration: 'none' }}
                        >
                            Sprawdź jak to działa na SprawdzStatus.pl 🚀
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    if (!instance) {
        return (
            <div className="public-update">
                <div className="container">
                    <div className="error-state card">
                        <div className="error-icon">⚠️</div>
                        <h2>Śledzenie nie zostało znalezione</h2>
                        <p className="text-muted">
                            Link może być nieprawidłowy lub śledzenie zostało usunięte.
                        </p>
                    </div>
                </div>
            </div>
        );
    }


    const statuses = instance?.templateSnapshot?.statuses || [];
    const currentStatusIndex = statuses.findIndex(s => s.name === instance?.currentStatus);
    const isChecklist = instance?.templateSnapshot?.templateType === 'checklist';
    // Checklists should NEVER show the final-status blocker — they are always re-submittable
    const isFinalStatus = !isChecklist && statuses.length > 0 && currentStatusIndex === statuses.length - 1 && instance.history?.length > 1;

    return (
        <div className="public-update">
            <SEO
                title={`Aktualizuj Status: ${instance?.templateSnapshot?.name || 'Ładowanie'} - ${instance ? instance.currentStatus : ''}`}
                description="Formularz aktualizacji statusu zamówienia"
            />
            <div className="container container-sm">

                <div className="tracking-summary-wrapper">
                    <div className="header card">
                        {instance.logoUrl ? (
                            // Jeśli firma wgrała własne logo, pokazujemy TYLKO je (bez błyskawicy)
                            <div className="business-logo" style={{ textAlign: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border-color)' }}>
                                <img
                                    src={`/api/${instance.logoUrl}`}
                                    alt="Logo Firmy"
                                    style={{ maxHeight: '100px', maxWidth: '300px', objectFit: 'contain' }}
                                />
                            </div>
                        ) : (
                            // Jeśli nie ma własnego logo, pokazujemy logo serwisu (błyskawica)
                            <div className="header-content">
                                <div className="app-logo">
                                    <svg width="40" height="40" fill="#F5DF4E" stroke="#F5DF4E" strokeWidth="2" viewBox="0 0 24 24">
                                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                                    </svg>
                                </div>
                            </div>
                        )}
                        <div className="header-text">
                            {instance?.templateSnapshot?.description && (
                                <p className="description">{instance.templateSnapshot.description}</p>
                            )}
                            {instance.deactivationDate && (
                                <div className="deactivation-notice" style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
                                    <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠️ Czas na aktualizację do: </span>
                                    <br />
                                    {new Date(instance.deactivationDate).toLocaleString('pl-PL')}
                                </div>
                            )}
                        </div>
                    </div>



                    {/* Instance Info / Tracking Data */}
                    {(() => {
                        const visibleFields = instance.fieldValues && Object.entries(instance.fieldValues)
                            .filter(([key]) => isNaN(key))
                            .filter(([key]) => {
                                const field = instance.templateSnapshot?.customFields?.find(f => f.name === key);
                                return field?.showInPublicView === true;
                            });

                        if (!visibleFields || visibleFields.length === 0) return null;

                        return (
                            <div className="card instance-info" style={{ marginBottom: '1.5rem' }}>
                                <h3 style={{ marginBottom: '1rem' }}>Dane:</h3>
                                <div className="info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                                    {visibleFields.map(([key, value]) => {
                                        const field = instance.templateSnapshot?.customFields?.find(f => f.name === key);
                                        // Helper to format label if missing
                                        const formatLabel = (str) => {
                                            return str
                                                .replace(/([A-Z])/g, ' $1') // insert space before capital letters
                                                .replace(/^./, str => str.toUpperCase()) // uppercase the first character
                                                .trim();
                                        };

                                        const label = field?.label || formatLabel(key);

                                        return (
                                            <div key={key} className="info-item">
                                                <span className="info-label" style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{label}:</span>
                                                <span className="info-value" style={{ fontWeight: 500, fontSize: '1rem' }}>
                                                    {typeof value === 'object' && value !== null
                                                        ? JSON.stringify(value)
                                                        : value?.toString() || '-'}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Instructions Section - User-provided content */}
                    {(() => {
                        const validInstructions = instance.instructionsData?.filter(i => i.content && i.content.trim()) || [];

                        if (validInstructions.length === 0) return null;

                        return (
                            <div className="card instructions-card" style={{ marginBottom: '1.5rem' }}>
                                <h3 style={{ marginBottom: '1rem' }}>
                                    Instrukcje
                                </h3>
                                <div className="instructions-list">
                                    {validInstructions.map((instruction, idx) => (
                                        <details key={idx} className="instruction-item" style={{ marginBottom: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '0.5rem' }}>
                                            <summary style={{ cursor: 'pointer', fontWeight: 500, padding: '0.25rem', outline: 'none' }}>
                                                {instruction.name}
                                            </summary>
                                            <div className="instruction-content" style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                                                {instruction.content}
                                            </div>
                                        </details>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Update Form */}
                    {isFinalStatus ? (
                        <div className="card text-center" style={{ padding: '3rem 1rem' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏁</div>
                            <h2>Realizacja zakończona</h2>
                            <p className="text-muted">
                                Ten status osiągnął etap końcowy. Dalsze aktualizacje nie są możliwe.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="update-form card">
                            <h3>{isChecklist ? 'Wype\u0142nij formularz' : 'Zaktualizuj status'}</h3>

                            {isChecklist ? (
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 0 }}>
                                        {instance.history?.length > 1 ? '✅ Formularz został już wypełniony. Możesz wysłać ponownie.' : 'Wypełnij formularz poniżej.'}
                                    </p>
                                </div>
                            ) : null}

                            {!isChecklist && (
                                <div className="form-group">
                                    <label className="form-label">Wybierz nowy status</label>
                                    <div className="status-options">
                                        {statuses.map((status, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                className={`status-option ${formData.status === status.name ? 'selected' : ''}`}
                                                style={{
                                                    borderColor: status.color,
                                                    '--status-color': status.color
                                                }}
                                                onClick={() => handleStatusChange(status.name)}
                                            >
                                                <span className="status-icon"><StatusIcon iconId={status.icon} size={20} /></span>
                                                <span className="status-name">{status.name}</span>
                                                {status.description && (
                                                    <span className="status-desc">{status.description}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Dynamic Update Fields */}
                            {instance.templateSnapshot?.updateFields && instance.templateSnapshot.updateFields.length > 0 && (
                                <div className="dynamic-fields" style={instance.templateSnapshot?.templateType === 'checklist' ? { marginBottom: '1.5rem' } : { marginBottom: '1.5rem', border: '1px solid var(--border-color)', padding: '1rem', borderRadius: 'var(--radius-md)' }}>
                                    <h4 style={{ marginBottom: '1.5rem', marginTop: 0, fontSize: instance.templateSnapshot?.templateType === 'checklist' ? '1.5rem' : '1.1rem' }}>
                                        {instance.templateSnapshot?.templateType === 'checklist' ? 'Formularz do wypełnienia' : 'Dodatkowe informacje'}
                                    </h4>
                                    {instance.templateSnapshot.updateFields
                                        .filter(field => {
                                            // Show if no linked statuses (generic field) or if current status is in the list
                                            if (!field.linkedStatuses || field.linkedStatuses.length === 0) return true;
                                            return field.linkedStatuses.includes(formData.status);
                                        })
                                        .map((field, idx) => {
                                            const isChecklist = instance.templateSnapshot?.templateType === 'checklist';
                                            return (
                                            <div key={idx} className="form-group" style={isChecklist ? { padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem', border: '1px solid var(--border-color)' } : {}}>
                                                <label className="form-label" style={isChecklist ? { fontSize: '1.15rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', display: 'block' } : {}}>
                                                    {field.label}
                                                    {field.required && <span style={{ color: 'var(--danger)', marginLeft: '4px' }}>*</span>}
                                                </label>

                                                {field.type === 'textarea' ? (
                                                    <textarea
                                                        className="form-textarea"
                                                        value={formData.fieldValues[field.name] || ''}
                                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                        required={field.required}
                                                    />
                                                ) : field.type === 'checkbox' ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!formData.fieldValues[field.name]}
                                                            onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                                                            style={{ width: '20px', height: '20px' }}
                                                        />
                                                        <span>Tak</span>
                                                    </div>
                                                ) : field.type === 'select' ? (
                                                    <select
                                                        className="form-select"
                                                        value={formData.fieldValues[field.name] || ''}
                                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                        required={field.required}
                                                    >
                                                        <option value="">Wybierz...</option>
                                                        {field.options?.map((opt, i) => (
                                                            <option key={i} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                ) : field.type === 'radio' ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        {field.options?.map((opt, i) => (
                                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                                <input
                                                                    type="radio"
                                                                    name={`field_${field.name}`}
                                                                    value={opt}
                                                                    checked={formData.fieldValues[field.name] === opt}
                                                                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                                    required={field.required}
                                                                />
                                                                <span>{opt}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                ) : field.type === 'photo' ? (
                                                    <div>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => {
                                                                if (e.target.files && e.target.files[0]) {
                                                                    handleFieldChange(field.name, e.target.files[0]);
                                                                }
                                                            }}
                                                            required={field.required && !formData.fieldValues[field.name]}
                                                            className="form-input-file"
                                                            id={`photo-${field.name}`}
                                                            style={{ display: 'none' }}
                                                        />
                                                        <label htmlFor={`photo-${field.name}`} className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-block', marginBottom: '0.5rem' }}>
                                                            📷 Wybierz zdjęcie
                                                        </label>
                                                        {formData.fieldValues[field.name] instanceof File && (
                                                            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <span style={{ fontSize: '1.2rem' }}>✅</span>
                                                                <span style={{ fontSize: '0.85rem', color: 'var(--success)' }}>
                                                                    {formData.fieldValues[field.name].name}
                                                                </span>
                                                                <button type="button" onClick={() => handleFieldChange(field.name, null)} style={{ border: 'none', background: 'none', color: 'var(--danger)', cursor: 'pointer', marginLeft: '0.5rem' }}>Usuń</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <input
                                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : 'text'}
                                                        className="form-input"
                                                        value={formData.fieldValues[field.name] || ''}
                                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                        required={field.required}
                                                    />
                                                )}
                                            </div>
                                        )})}
                                </div>
                            )}

                            {/* Notes – hide for checklists (they have their own fields) */}
                            {!isChecklist && (
                                <div className="form-group notes">
                                    <label className="form-label">Notatki (opcjonalne)</label>
                                    <textarea
                                        className="form-textarea"
                                        rows="3"
                                        placeholder="Dodatkowe informacje..."
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    />
                                </div>
                            )}

                            {/* General Photo Upload – hide for checklists (each field has its own photo input) */}
                            {!isChecklist && instance.templateSnapshot?.settings?.enablePhotos && (
                                <div className="form-group photos">
                                    <label className="form-label">Zdjęcia (opcjonalne)</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handlePhotoChange}
                                        className="form-input-file"
                                        style={{ display: 'none' }}
                                        id="photo-upload"
                                    />
                                    <label htmlFor="photo-upload" className={`btn btn-outline ${compressing ? 'disabled' : ''}`} style={{ cursor: compressing ? 'wait' : 'pointer', marginBottom: '1rem' }}>
                                        {compressing ? '⌛ Przetwarzanie zdjęć...' : '📷 Dodaj zdjęcia'}
                                    </label>

                                    {formData.photos.length > 0 && (
                                        <div className="photo-preview-grid">
                                            {formData.photos.map((photo, index) => (
                                                <div key={index} className="photo-preview-item">
                                                    <img
                                                        src={URL.createObjectURL(photo)}
                                                        alt={`Preview ${index + 1}`}
                                                        className="photo-preview-img"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removePhoto(index)}
                                                        className="photo-remove-btn"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                                        Możesz dodać zdjęcia do aktualizacji statusu
                                    </p>
                                </div>
                            )}

                            {/* Reporter Info - Only if enabled in template settings */}
                            {(instance.templateSnapshot?.settings?.enableReporterInfo !== false) && (
                                <div className="reporter-section">
                                    <h3>Twoje dane (opcjonalne)</h3>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="form-label">
                                                Imię i nazwisko
                                                {instance.templateSnapshot?.settings?.requireReporterInfo && <span style={{ color: 'var(--danger)', marginLeft: '4px' }}>*</span>}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="Jan Kowalski"
                                                name="name"
                                                value={formData.reporterInfo.name}
                                                onChange={handleReporterChange}
                                                required={instance.templateSnapshot?.settings?.requireReporterInfo}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Telefon</label>
                                            <input
                                                type="tel"
                                                className="form-input"
                                                placeholder="123 456 789"
                                                name="phone"
                                                value={formData.reporterInfo.phone}
                                                onChange={handleReporterChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                disabled={submitting || (!formData.status && instance.templateSnapshot?.templateType !== 'checklist')}
                            >
                                {submitting ? 'Przesyłanie...' : (instance.templateSnapshot?.templateType === 'checklist' ? 'Wyślij formularz' : 'Zaktualizuj status')}
                            </button>
                            {success && (
                                <div className="success-banner" style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid var(--success)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-primary)' }}>
                                    <div style={{ fontSize: '1.25rem' }}>✅</div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Status zaktualizowany!</h3>
                                    </div>
                                </div>
                            )}
                        </form>
                    )}

                    {/* Current Status – hide for checklists */}
                    {!isFinalStatus && !isChecklist && (
                        <div className="current-status card">
                            <h3>Aktualny status</h3>
                            <div className="status-current">
                                {statuses.find(s => s.name === instance.currentStatus) && (
                                    <div className="status-badge-current">
                                        <span className="status-icon">
                                            <StatusIcon iconId={statuses.find(s => s.name === instance.currentStatus)?.icon} size={24} />
                                        </span>
                                        <span className="status-name">{instance.currentStatus}</span>
                                    </div>
                                )}
                            </div>
                            <p className="text-muted status-time">
                                Ostatnia aktualizacja: {new Date(instance.updatedAt).toLocaleString('pl-PL')}
                            </p>
                        </div>
                    )}
                </div >

                <div className="marketing-banner">
                    <p>Chcesz taki system powiadomień w swojej firmie?</p>
                    <a
                        href="https://sprawdzstatus.pl"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline"
                    >
                        Sprawdź jak to działa na SprawdzStatus.pl 🚀
                    </a>
                </div>
            </div >
        </div >
    );
};


export default PublicStatusUpdate;
